import * as React from 'react';

// API
import apibridge from '../../apibridge';

// UTILS
import { validationErrorsToString } from '../../utils';

// PACKAGES
import { useParams } from 'react-router-dom';
import { SwitchTransition, CSSTransition } from 'react-transition-group';

// COMPONENTS
import PageName from '../../components/PageName';
import LoadingSpinner from '../../components/LoadingSpinner';
import ScrollToTopButton from '../../components/ScrollToTopButton';
import ProfileSummary from '../../components/ProfileSummary';
import ProfileLinkedAccounts from '../../components/ProfileLinkedAccounts';
import ProfileOrganisations from '../../components/ProfileOrganisations';
import ProfileComplaints from '../../components/ProfileComplaints';
import ProfileFeedback from '../../components/ProfileFeedback';
import ProfileIncidents from '../../components/ProfileIncidents';
import ProfilePolicies from '../../components/ProfilePolicies';
import ProfileTraining from '../../components/ProfileTraining';
import GlobalModal from '../../components/GlobalModal';
import ChildrenCheckModal from './ChildrenCheckModal';
import StatusIcon from '../../components/StatusIcon';
import Button from '../../components/Button';

// TYPES
import { RouteParams } from '../../Interfaces';
import { DomainProfileType, FamiliesPastFamilyMember } from '../../api/models';

const PastFamiliesProfile: React.FC = () => {
	// Error and Loading state
	const [formErrorMessage, setFormErrorMessage] = React.useState<string>('');

	const [isLoading, setIsLoading] = React.useState<boolean>(true);

	// getting the id from params
	const params = useParams<RouteParams>();
	const familiesId = params.id;

	// Set up data state and initial data
	const initialPastProfileData: FamiliesPastFamilyMember = {
		id: undefined,
		childAwareId: undefined,
		profileImage: undefined,
		firstName: undefined,
		lastName: undefined,
		email: undefined,
		profileType: undefined,
		canUpdate: undefined,

		certifications: [],
		organisations: [],
		linkedAccounts: [],
		complaints: [],
		feedback: [],
		incidents: [],
		policies: [],
		trainings: []
	};

	const [profileData, setProfileData] = React.useState<FamiliesPastFamilyMember>(initialPastProfileData);

	const getPastFamiliesProfileData = async () => {
		setIsLoading(true);

		const response = await apibridge.getFamiliesPast(familiesId);

		if (response.isError) {
			setIsLoading(false);

			setFormErrorMessage(
				response.validationErrors.length
					? validationErrorsToString(response.validationErrors)
					: response.message || 'An error has occurred'
			);
		} else {
			const { result = initialPastProfileData } = response;

			setIsLoading(false);

			setProfileData(result);
		}
	};

	React.useEffect(() => {
		getPastFamiliesProfileData();
	}, []);

	const {
		id,
		childAwareId,
		userId,
		email,
		profileImage,
		firstName,
		lastName,
		profileType,

		certifications,
		linkedAccounts,
		organisations,
		complaints,
		feedback,
		incidents,
		policies,
		trainings,

		canUpdate,
		isCurrentMember
	} = profileData;

	const displayProfileName = (profileType?: string) => {
		if (profileType === DomainProfileType.Parent) {
			return 'Past Parent';
		} else if (profileType === DomainProfileType.Student) {
			return 'Past Child';
		} else return 'Past Profile';
	};

	const displayProfileRole = (profileType?: string) => {
		if (profileType === DomainProfileType.Parent) {
			return <>Parent or Parent Volunteer</>;
		} else if (profileType === DomainProfileType.Student) {
			return <>Child or Student</>;
		} else return null;
	};

	// Maximum number of items per section
	const defaultMaxItems: number = 4;

	//Check if user is a student
	const isStudent = profileType === DomainProfileType.Student;

	// State to manage Modals
	const [showChildrenCheckModal, setShowChildrenCheckModal] = React.useState<boolean>(false);

	// State to manage Document Id
	const [documentId, setDocumentId] = React.useState<string>('');

	return (
		<div className="page-general-layout">
			{!!formErrorMessage ? (
				<div className="form-validation-error">{formErrorMessage}</div>
			) : (
				<SwitchTransition mode="out-in">
					<CSSTransition key={`key_${isLoading}`} timeout={600} classNames="standalone-fade" mountOnEnter unmountOnExit>
						{isLoading ? (
							<LoadingSpinner show={isLoading} />
						) : (
							<>
								<PageName title={displayProfileName(profileType)} showBack={true} />

								<ProfileSummary
									profileImage={profileImage}
									firstName={firstName}
									lastName={lastName}
									childAwareId={childAwareId}
									email={email}
									isStudent={isStudent}
									isPastProfile
								/>

								{/* ACCOUNT INFO */}
								<section className="info-section">
									<div className="section-header">
										<h2 className="header-name color-dark">Account information</h2>
									</div>

									{childAwareId && (
										<div className="row-container">
											<div className="row detail-row">
												<div className="col-12 pdl-0">
													<div className="row detail-info">
														<div className="col-lg-4 pdl-0 detail-name bold">ChildAware ID</div>

														<div className="col-lg-8 pdl-0 detail-status account">{childAwareId}</div>
													</div>
												</div>
											</div>
										</div>
									)}

									{email && (
										<div className="row-container">
											<div className="row detail-row">
												<div className="col-12 pdl-0">
													<div className="row detail-info">
														<div className="col-lg-4 pdl-0 detail-name bold">Email</div>

														<div className="col-lg-8 pdl-0 detail-status account">{email}</div>
													</div>
												</div>
											</div>
										</div>
									)}
								</section>

								{/* DOCUMENTS */}
								{certifications && certifications.length > 0 ? (
									<section className="info-section">
										<div className="section-header">
											<h2 className="header-name color-dark">Documents</h2>
										</div>

										{certifications.map((item, index) => {
											return (
												<div key={index} className="row-container">
													<div className="row detail-row">
														<div className="col-auto pdl-0 pdr-0">
															<StatusIcon statusType={item.status || 'Error'} />
														</div>

														<div className="col-md-auto col-lg-10 full-width">
															<div className="row detail-info">
																<div className="col-lg-4 detail-name">{item.displayName}</div>

																<div className="col-lg-8 detail-status">
																	<span className={`label ${item.status?.toLowerCase()}`}>{item.statusLabel}</span>
																</div>
															</div>
														</div>

														<div className="col-auto pdr-0 link-group">
															<Button
																className="btn-text"
																type="button"
																onClick={() => {
																	setShowChildrenCheckModal(true);
																	setDocumentId(item.id || '');
																}}
															>
																View
															</Button>
														</div>
													</div>
												</div>
											);
										})}
									</section>
								) : null}

								{/* ORGANISATIONS */}
								{profileType !== DomainProfileType.Student && (
									<ProfileOrganisations
										organisations={organisations}
										isStudent={profileType === DomainProfileType.Student}
									/>
								)}

								{/* LINKED ACCOUNT */}
								<ProfileLinkedAccounts linkedAccounts={linkedAccounts} userId={userId} isPastProfile />

								{/* COMPLAINTS */}
								<ProfileComplaints complaints={complaints} defaultMaxItems={defaultMaxItems} isPastProfile />

								{/* FEEDBACK */}
								<ProfileFeedback feedback={feedback} defaultMaxItems={defaultMaxItems} isPastProfile />

								{/* INCIDENTS REPORT */}
								<ProfileIncidents incidents={incidents} defaultMaxItems={defaultMaxItems} isPastProfile />

								{/* POLICIES */}
								<ProfilePolicies policies={policies} defaultMaxItems={defaultMaxItems} isPastProfile />

								{/* TRAINING*/}
								<ProfileTraining training={trainings} defaultMaxItems={defaultMaxItems} isPastProfile />

								<GlobalModal
									isOpen={showChildrenCheckModal}
									titleText="Working With Children Check"
									setIsOpen={setShowChildrenCheckModal}
									functionOnClose={getPastFamiliesProfileData}
								>
									<ChildrenCheckModal
										getFamiliesProfileData={getPastFamiliesProfileData}
										canUpdate={canUpdate}
										documentId={documentId}
										profileData={profileData}
									/>
								</GlobalModal>

								<ScrollToTopButton extraClass="mtop-40" />
							</>
						)}
					</CSSTransition>
				</SwitchTransition>
			)}
		</div>
	);
};

export default PastFamiliesProfile;
